// ariaAudioBus.js — REAL AMPLITUDE OFF HER VOICE (Braeden 8/14)
//
//   "if you can add a taling ball kind of like what open ai does"
//
// The ball has to move with what she is ACTUALLY saying, otherwise it is a
// screensaver — a loop that pulses through her silences reads as fake within
// about two seconds.
//
// SAFETY FIRST: this sits on the live voice path. createMediaElementSource
// re-routes an element's audio into the graph, so if the analyser is attached
// and NOT connected onward to the destination, she goes silent. Every path
// here connects to destination, and every failure falls back to "no analyser"
// rather than to "no sound". Her voice working matters more than the visual.

let ctx = null;
let analyser = null;
let data = null;
const sourced = new WeakSet();   // an element may only be sourced once, ever

function ensure() {
  if (ctx) return true;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    ctx = new AC();
    analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.75;   // the eye hates jitter
    data = new Uint8Array(analyser.frequencyBinCount);
    analyser.connect(ctx.destination);
    return true;
  } catch (_) { ctx = null; analyser = null; return false; }
}

/**
 * Route one <audio> through the analyser. Silent no-op on any failure — the
 * ball falls back to its synthetic pulse and her voice is untouched.
 */
export function attach(audio) {
  if (!audio || sourced.has(audio)) return false;
  if (!ensure()) return false;
  try {
    const src = ctx.createMediaElementSource(audio);
    src.connect(analyser);          // analyser is already wired to destination
    sourced.add(audio);
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return true;
  } catch (_) {
    // already sourced elsewhere, or a cross-origin element — leave it alone
    return false;
  }
}

/** 0..1 loudness right now. 0 when nothing is routed or nothing is playing. */
export function level() {
  if (!analyser || !data) return 0;
  try {
    analyser.getByteTimeDomainData(data);
    let peak = 0;
    for (let i = 0; i < data.length; i++) {
      const v = Math.abs(data[i] - 128) / 128;
      if (v > peak) peak = v;
    }
    // speech peaks land low; lift it so the ball actually moves
    return Math.min(1, peak * 2.4);
  } catch (_) { return 0; }
}

export function isLive() { return !!analyser; }

// ── HIS VOICE, NOT JUST HERS (Braeden 8/31) ──────────────────────────────
//
//   "when im talking into the mic, it needs to show sound wages in the circle"
//
// The ring has always moved with HER voice (the analyser above) and sat
// perfectly still while he talked — listening was a solid blue ring by design,
// so there was no feedback that the mic was actually picking him up. That is
// the same silence problem as the mute reply: the only way to tell it is
// working is to see it move.
//
// The capture path already has his audio as 16k Int16 PCM (streamingTranscribe
// posts a chunk every 100ms), so the level costs one RMS pass over a buffer we
// already hold — no second analyser, no second AudioContext, nothing extra to
// keep alive on a phone.
let _micLevel = 0;
let _micAt = 0;

/** Publish a 0..1 loudness from a raw Int16 PCM chunk. */
export function pushMicPcm(buf) {
  try {
    const pcm = new Int16Array(buf);
    if (!pcm.length) return;
    let sum = 0;
    // Every 4th sample is plenty at 1600 samples per chunk and a quarter of
    // the work on a phone.
    for (let i = 0; i < pcm.length; i += 4) { const v = pcm[i] / 32768; sum += v * v; }
    const rms = Math.sqrt(sum / Math.ceil(pcm.length / 4));
    // Speech RMS lands around 0.02-0.25. A straight multiplier pinned normal
    // talking at 1.0 (measured), which is a ring that stops moving exactly
    // when he is speaking normally - so a compressive curve instead: quiet
    // speech is clearly visible, loud has somewhere left to go.
    const lvl = Math.max(0, Math.min(1, Math.pow(rms * 3.2, 0.7)));
    // Rise fast (the eye should catch the attack), fall slow (no strobing).
    _micLevel = lvl > _micLevel ? lvl : _micLevel * 0.82 + lvl * 0.18;
    _micAt = Date.now();
  } catch (_) { /* the visual never costs the turn */ }
}

/** 0..1, decaying to 0 when nothing has arrived for a moment. */
export function micLevel() {
  if (!_micAt || Date.now() - _micAt > 400) { _micLevel *= 0.6; if (_micLevel < 0.01) _micLevel = 0; }
  return _micLevel;
}
